import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { getOrderStateTranslationKey, ORDER_STATES } from "@msuite/utils/stock";
import type { Enums } from "@msuite/utils/types";

/** Props Interface */
interface OrdersStateFilterProps {
	state: Enums<"orderState"> | "all";
	onChange: (state: Enums<"orderState"> | "all") => void;
}

/**
 * The order list's state filter — the lifecycle in reading order, with `all` as the leading
 * segment. One state at a time: the segments carry the whole lifecycle, so which one the list is
 * showing is answered by looking at the control rather than by opening it.
 *
 * It narrows on the server rather than in the table. The list has never been paginated and
 * returns every order the organisation ever raised, so filtering here would still fetch all of
 * them; the same query param also serves the native list, which has no column filters to fall
 * back on.
 *
 * `all` sends no state param at all, and it is the only segment that shows finished and cancelled
 * orders alongside live ones.
 */
export function OrdersStateFilter({ state, onChange }: OrdersStateFilterProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<SegmentedControl
			value={state}
			onChange={onChange}
			items={[
				{
					value: "all" as const,
					label: t("stock.filter_state_all"),
					cyId: "orders-state-filter-all",
				},
				...ORDER_STATES.map((orderState) => ({
					value: orderState,
					label: t(getOrderStateTranslationKey(orderState)),
					cyId: `orders-state-filter-${orderState}`,
				})),
			]}
		/>
	);
}
